/**
 * Finding Detail View — Tabbed deep reading + cross-check.
 *
 * Renders inside the findings panel when a finding card is clicked.
 * Three tabs:
 * - Summary: breadcrumb, title, "Why Cited", "Agent's Reference", metadata
 * - Source: full-width responsive iframe (EUR-Lex article-only / PDF / Q&A text)
 * - Links: 3-column grid (References | Q&A | Concepts) + inline preview
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ExternalLink, Globe, MessageSquare, ArrowUpRight, ArrowDownLeft, Loader2, BookOpen, Maximize2 } from 'lucide-react';
import {
  formatContent,
  formatQaContent,
  lightTheme,
  cleanSourceText,
  resolveAgentReferenceText,
} from '../../utils/contentFormatter';
import type { CitationContext } from './UnifiedFindingsPanel';
import type { DictMarker } from '../../types/workspaceState';
import FindingFocusModal from './FindingFocusModal';
import ReferencePreview, { type PreviewRef } from './ReferencePreview';
import { compactList, formatQaDate } from './qaUtils';
import {
  detectFindingSourceType,
  getSafeSourceUrl,
  isMeaningful,
  type SourceType,
} from '../../utils/findingSourceType';
import {
  buildEurLexUrl,
  buildEurLexViewerUrl,
  buildFindingSourceViewerUrl,
  buildPdfViewerUrl,
  EURLEX_PREVIEW_SANDBOX,
  EURLEX_PREVIEW_SRCDOC_SANDBOX,
  extractPdfDocIdFromFields,
  findingAsOf,
  findPeerDocumentUrl,
} from '../../utils/findingSourceLinks';
import { useSourceDocument } from '../../hooks/useSourceDocument';
import { formatCount } from '../../utils/plural';

// =============================================================================
// TYPES
// =============================================================================

interface Finding {
  finding_id: string;
  source_document_celex?: string;
  source_document_name?: string;
  source_reference_hierarchy?: string;
  source_reference?: string;
  source_text?: string;
  source_snippet?: string;
  extracted_at?: string;
  tool_used?: string;
  document_version?: string;
  valid_from?: string;       // BAR-533 version pin
  valid_to?: string;
  version_status?: string;
  source_service?: string;
  source_url?: string;
  source_type?: string;
  document_type?: string;
  document_full_title?: string;
  article_title?: string;
  // Legal classification
  legal_status?: string;
  // PDF positional data
  chunk_id?: string;
  page_number?: number;
  paragraph_id?: string;
  bbox?: Record<string, any>;
  // Authority and lifecycle metadata (PDF service)
  authority_abbrev?: string;
  authority_name_en?: string;
  lifecycle_stage?: string;
  reference_type?: string;
  pdf_url?: string;
  page_url?: string;
  // Canonical metadata (PDF service)
  publication_date?: string;
  regulatory_framework?: string[];
  legal_basis?: string[];
  applies_to?: string[];
  effective_date?: string;
  parent_reference?: string;
  replaces?: string;
  amends?: string[];
  compliance_deadline?: string;
  // Graph intelligence
  cross_references?: Array<{ direction: string; article: string; celex: string; document_name: string }>;
  concepts?: string[];
  linked_qa?: Array<{ ref: string; target: string; date?: string; title?: string }>;
  qa_topic?: string;
  qa_legal_act?: string;
  qa_status?: string;
  qa_submitted_date?: string;
  qa_clarifies?: string[];
  // Concept-specific structured data
  definition_sources?: Array<{ ref: string; celex: string; short_name: string }>;
  concept_relationships?: Record<string, string[]>;
}

const PDF_IFRAME_SPINNER_TIMEOUT_MS = 3000;
const EURLEX_IFRAME_FAILURE_TIMEOUT_MS = 45000;

interface FindingDetailViewProps {
  finding: Finding;
  citationContext?: CitationContext | null;
  allFindings: Record<string, Finding>;
  allMarkers?: Record<string, DictMarker>;
  workspaceId?: string;
  compartmentId?: string;
  onNavigateToNode?: (nodeId: string) => void;
  onFindingClick: (findingId: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Strip graph noise from article names: "(Consolidated TEXT: 3...)" etc. */
function cleanArticleName(article: string): string {
  return article
    .replace(/\s*\(Consolidated TEXT:.*?\)/gi, '')
    .replace(/\s*\(Consolidated\s.*?\)/gi, '')
    .replace(/^Art\.\s+/, 'Article ')
    .trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripLeadingReference(title: string, reference: string | undefined | null): string {
  if (!title || !reference) return title;
  return title
    .replace(new RegExp(`^\\s*${escapeRegExp(reference)}\\s*[-–—:]?\\s*`, 'i'), '')
    .trim();
}

// =============================================================================
// SHARED SECTIONS
// =============================================================================

/** Breadcrumb from hierarchy */
function Breadcrumb({ hierarchy }: { hierarchy: string }) {
  const parts = hierarchy.split('>').map(s => s.trim().replace(/\s*\(.*?\)\s*$/, '')).filter(Boolean);
  return (
    <div className="flex items-center gap-1 text-[11px] text-fg-tertiary flex-wrap">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-fg-secondary">/</span>}
          <span>{part}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

/** Compact metadata strip for PDF findings — authority, lifecycle, dates, framework pills */
function PdfMetadataStrip({ finding }: { finding: Finding }) {
  const parts: string[] = [];
  if (finding.authority_abbrev) parts.push(finding.authority_abbrev);
  if (finding.document_type) parts.push(finding.document_type);
  if (finding.lifecycle_stage) parts.push(finding.lifecycle_stage);

  const hasFramework = finding.regulatory_framework && finding.regulatory_framework.length > 0;
  const hasDate = finding.effective_date || finding.publication_date;

  if (!parts.length && !hasFramework && !hasDate) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap mt-1.5">
      {parts.length > 0 && (
        <span className="text-[10px] text-fg-tertiary font-medium">
          {parts.join(' \u00b7 ')}
        </span>
      )}
      {hasDate && (
        <span className="text-[10px] text-fg-tertiary">
          {finding.effective_date ? `Eff. ${finding.effective_date}` : `Pub. ${finding.publication_date}`}
        </span>
      )}
      {hasFramework && (
        <div className="flex gap-1">
          {finding.regulatory_framework!.map((fw, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-accent-tint text-accent font-medium">
              {fw}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function QaAboutSection({ finding }: { finding: Finding }) {
  const authority = finding.authority_abbrev || finding.authority_name_en || 'EBA';
  const appliesTo = isMeaningful(finding.qa_legal_act)
    ? [finding.qa_legal_act]
    : compactList([
        ...(finding.qa_clarifies || []),
        ...(finding.cross_references || [])
          .filter((xref) => xref.direction === 'outgoing')
          .map((xref) => cleanArticleName(xref.article)),
      ]);
  const rows = [
    { label: 'Question ID', value: finding.source_reference },
    { label: 'Status', value: finding.qa_status },
    { label: 'Prepared by', value: authority },
    { label: 'Submitted', value: formatQaDate(finding.qa_submitted_date) },
    { label: 'Final answer published', value: formatQaDate(finding.publication_date) },
    { label: 'Topic', value: finding.qa_topic },
    { label: 'Applies to', value: appliesTo.length ? appliesTo.join(', ') : undefined },
  ].filter((row) => isMeaningful(row.value));

  if (!rows.length) return null;

  return (
    <div className="mb-4">
      <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-2">
        About this Q&amp;A
      </div>
      <dl className="grid grid-cols-[132px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <dt className="text-fg-tertiary">{row.label}</dt>
            <dd className="text-fg-secondary min-w-0 break-words">{row.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

/** Context tab for PDF findings — regulatory framework, legal basis, lineage, dates */
function PdfContextTab({ finding }: { finding: Finding }) {
  const hasFramework = finding.regulatory_framework && finding.regulatory_framework.length > 0;
  const hasLegalBasis = finding.legal_basis && finding.legal_basis.length > 0;
  const hasAppliesTo = finding.applies_to && finding.applies_to.length > 0;
  const hasLineage = finding.parent_reference || finding.replaces || (finding.amends && finding.amends.length > 0);
  const hasDates = finding.publication_date || finding.effective_date || finding.compliance_deadline;
  const hasAnything = hasFramework || hasLegalBasis || hasAppliesTo || hasLineage || hasDates || finding.legal_status;

  if (!hasAnything) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-fg-tertiary">
        No regulatory context available for this finding.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {/* Regulatory framework pills */}
      {hasFramework && (
        <div>
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Regulatory framework</div>
          <div className="flex flex-wrap gap-1.5">
            {finding.regulatory_framework!.map((fw, i) => (
              <span key={i} className="px-2.5 py-1 rounded text-xs bg-accent-tint text-accent-hover font-medium">{fw}</span>
            ))}
          </div>
        </div>
      )}

      {/* Legal basis + applies to — side by side */}
      {(hasLegalBasis || hasAppliesTo) && (
        <div className="grid grid-cols-2 gap-4">
          {hasLegalBasis && (
            <div>
              <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Legal basis</div>
              <ul className="space-y-0.5">
                {finding.legal_basis!.map((basis, i) => (
                  <li key={i} className="text-xs text-fg-secondary flex items-start gap-1.5">
                    <span className="text-fg-tertiary mt-0.5">&#8226;</span>
                    <span>{basis}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hasAppliesTo && (
            <div>
              <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Applies to</div>
              <ul className="space-y-0.5">
                {finding.applies_to!.map((entity, i) => (
                  <li key={i} className="text-xs text-fg-secondary flex items-start gap-1.5">
                    <span className="text-fg-tertiary mt-0.5">&#8226;</span>
                    <span>{entity}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Legal status */}
      {finding.legal_status && (
        <div>
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Legal status</div>
          <span className="text-xs text-fg-secondary">
            {finding.legal_status.replace(/_/g, ' ')}
            {finding.document_type && finding.lifecycle_stage && (
              <span className="text-fg-tertiary"> ({finding.document_type} \u00b7 {finding.lifecycle_stage})</span>
            )}
          </span>
        </div>
      )}

      {/* Document lineage */}
      {hasLineage && (
        <div>
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Document lineage</div>
          <div className="space-y-1 text-xs text-fg-secondary">
            {finding.parent_reference && (
              <div><span className="text-fg-tertiary w-16 inline-block">Parent:</span> {finding.parent_reference}</div>
            )}
            {finding.replaces && (
              <div><span className="text-fg-tertiary w-16 inline-block">Replaces:</span> {finding.replaces}</div>
            )}
            {finding.amends && finding.amends.length > 0 && (
              <div><span className="text-fg-tertiary w-16 inline-block">Amends:</span> {finding.amends.join(', ')}</div>
            )}
          </div>
        </div>
      )}

      {/* Dates */}
      {hasDates && (
        <div>
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Dates</div>
          <div className="space-y-1 text-xs text-fg-secondary">
            {finding.publication_date && (
              <div><span className="text-fg-tertiary w-20 inline-block">Published:</span> {finding.publication_date}</div>
            )}
            {finding.effective_date && (
              <div><span className="text-fg-tertiary w-20 inline-block">Effective:</span> {finding.effective_date}</div>
            )}
            {finding.compliance_deadline && (
              <div><span className="text-fg-tertiary w-20 inline-block">Compliance:</span> {finding.compliance_deadline}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** "Why Cited" callout */
function WhyCited({ context }: { context: string }) {
  return (
    <div className="p-3 bg-accent-tint rounded-lg border-l-4 border-accent">
      <div className="text-[10px] font-semibold text-accent-hover uppercase tracking-wide mb-1">Why cited</div>
      <p className="text-xs text-fg leading-relaxed">{context}</p>
    </div>
  );
}

/** External link button */
function ExternalLinkButton({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-xs text-accent hover:text-accent-hover hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      <Globe size={12} />
      {label}
      <ExternalLink size={10} />
    </a>
  );
}

// =============================================================================
// TAB: FINDING (merged summary + source)
// =============================================================================

/**
 * FindingTab — Agent's reference at top, source content below.
 * For EUR-Lex/PDF: reference text section + full-width iframe.
 * For Q&A/concept/unknown: reference text + text content.
 */
function FindingTab({
  finding,
  citationContext,
  findingMarkers,
  fallbackReference,
  sourceType,
  actionLink,
  eurLexUrl,
}: {
  finding: Finding;
  citationContext?: CitationContext | null;
  findingMarkers: DictMarker[];
  fallbackReference: string | null;
  sourceType: SourceType;
  actionLink: { url: string; label: string } | null;
  eurLexUrl: string | null;
}) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // The embedded source-document URL (EUR-Lex article with highlight, or
  // PDF viewer). Shared with the background prefetch via one builder so a
  // prefetched URL is byte-identical to what this iframe loads.
  const iframeSrc = useMemo(() => buildFindingSourceViewerUrl(finding), [finding]);
  const hasIframe = (sourceType === 'eurlex' || sourceType === 'pdf') && !!iframeSrc;

  // Instant open: if the EUR-Lex HTML was prefetched into memory, render it
  // via srcDoc — zero network on open. Falls back to the network iframe
  // (src) when not yet warmed or for PDFs.
  const cachedDoc = useSourceDocument(hasIframe ? iframeSrc : null);
  const liveHtml = cachedDoc?.status === 'ready' ? cachedDoc.html : null;

  // Commit the render mode when the finding (iframeSrc) changes. If a warm
  // completes WHILE this finding is open, we must NOT swap the already-rendered
  // network iframe over to srcDoc — that remounts it, flashing and resetting
  // scroll. So freeze the choice at open: cached-at-open → srcDoc (instant);
  // not-cached-at-open → network iframe for this finding's lifetime.
  const committedRender = useRef<{ src: string | null; html: string | null }>({
    src: null,
    html: null,
  });
  if (committedRender.current.src !== (iframeSrc ?? null)) {
    committedRender.current = { src: iframeSrc ?? null, html: liveHtml };
  }
  const cachedHtml = committedRender.current.html;

  // Reset iframe state when switching findings. Cached HTML renders instantly
  // (no network), so don't show the loading skeleton for it. cachedHtml is
  // committed per-finding, so this only re-runs on an actual finding switch.
  useEffect(() => {
    setIframeLoading(!cachedHtml);
    setIframeError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeSrc]);

  // Iframe timeout — for PDFs, just hide spinner after 3s (plugin doesn't fire onLoad).
  // EUR-Lex can include a Cloud Run cold start + cache fetch; keep the
  // in-flight iframe loading before surfacing a real stuck-load fallback.
  useEffect(() => {
    if (!hasIframe || !iframeLoading) return;
    const timer = setTimeout(() => {
      if (sourceType === 'pdf') {
        setIframeLoading(false); // PDF is rendering, just hide spinner
      } else {
        setIframeLoading(false);
        setIframeError(true);
      }
    }, sourceType === 'pdf' ? PDF_IFRAME_SPINNER_TIMEOUT_MS : EURLEX_IFRAME_FAILURE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [iframeSrc, retryCount, hasIframe, iframeLoading, sourceType]);

  const hasReferenceText = findingMarkers.length > 0 || fallbackReference || citationContext?.context;

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Top section: Agent's reference text (collapsible height) */}
      {hasReferenceText && (
        <div className="flex-shrink-0 p-4 space-y-3 border-b border-divider max-h-[28%] overflow-y-auto">
          {/* Why Cited */}
          {citationContext?.context && <WhyCited context={citationContext.context} />}

          {/* Agent's References — multi-bullet from all markers */}
          {findingMarkers.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">
                Agent's reference{findingMarkers.length > 1 ? 's' : ''}
              </div>
              <ul className="space-y-2">
                {findingMarkers.map((marker) => {
                  const isActive = citationContext?.label === marker.label;
                  return (
                    <li
                      key={marker.marker_id}
                      className={`text-xs p-2.5 rounded-lg leading-relaxed ${
                        isActive
                          ? 'bg-accent-tint ring-1 ring-accent'
                          : 'bg-accent-tint/60'
                      }`}
                      style={{ borderLeft: '3px solid var(--accent)' }}
                    >
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-mono font-semibold text-accent-hover">{marker.label}</span>
                        {marker.citation_text && (
                          <span className="text-fg-secondary whitespace-pre-line">{marker.citation_text}</span>
                        )}
                      </div>
                      {marker.context && (
                        <p className="text-fg-tertiary mt-0.5 whitespace-pre-line">{marker.context}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Fallback: single Agent's Reference */}
          {fallbackReference && (
            <div>
              <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Agent's reference</div>
              <div className="text-xs bg-accent-tint/60 p-3 rounded-lg leading-relaxed whitespace-pre-line" style={{ borderLeft: '3px solid var(--accent)' }}>
                {fallbackReference}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom section: Source content (fills remaining height) */}
      {sourceType === 'concept' ? (
        <div className="flex-1 min-h-0">
          <ConceptContent finding={finding} />
        </div>
      ) : hasIframe ? (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="relative flex-1 min-h-0" style={{ minHeight: '500px' }}>
            {iframeLoading && !iframeError && (
              <SourceLoadingSkeleton sourceType={sourceType} />
            )}
            {iframeError && (
              <div className="absolute inset-0 flex items-center justify-center bg-bg-warm z-10">
                <div className="text-center space-y-3">
                  <p className="text-sm text-fg-tertiary">Unable to load source content</p>
                  <div className="flex items-center gap-3 justify-center">
                    <button
                      onClick={() => { setRetryCount(c => c + 1); setIframeLoading(true); setIframeError(false); }}
                      className="px-3 py-1.5 text-xs bg-accent text-white rounded hover:bg-accent-hover"
                    >
                      Retry
                    </button>
                    {eurLexUrl && (
                      <a
                        href={eurLexUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-xs border border-border-default text-fg-secondary rounded hover:bg-bg-warm"
                      >
                        Open on EUR-Lex
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            <iframe
              key={cachedHtml ? 'srcdoc' : retryCount}
              // Prefetched EUR-Lex HTML renders from memory (srcDoc, no
              // network); otherwise load the document over the network (src),
              // which is now browser-cacheable so re-opens are instant too.
              {...(cachedHtml ? { srcDoc: cachedHtml } : { src: iframeSrc! })}
              className="w-full h-full border-0"
              title={`Source: ${finding.source_reference}`}
              // Sandbox: PDFs need none (browser plugin). For EUR-Lex, srcDoc
              // inherits THIS app's origin, so it must NOT get allow-same-origin
              // — the fetched article HTML is third-party and its inline script
              // would otherwise run in our origin (cookies/session). The network
              // src path stays cross-origin (WOS), so allow-same-origin there
              // only retains WOS's own origin. Both let the preview's links open
              // a tab; see EURLEX_PREVIEW_SANDBOX.
              {...(sourceType === 'pdf'
                ? {}
                : { sandbox: cachedHtml ? EURLEX_PREVIEW_SRCDOC_SANDBOX : EURLEX_PREVIEW_SANDBOX }
              )}
              onLoad={() => { setIframeLoading(false); setIframeError(false); }}
              onError={() => { setIframeLoading(false); setIframeError(true); }}
            />
          </div>
          {/* Primary action now lives in the header (canonical, always visible).
              No duplicate footer link here. */}
        </div>
      ) : (
        /* Text-based source content */
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Primary action is in the header now — no inline duplicate here. */}

          {/* Q&A text */}
          {sourceType === 'qa' && finding.source_text && (
            <div>
              <QaAboutSection finding={finding} />
              <div className="p-3 bg-warning-bg/50 rounded-lg border border-warning">
                {formatQaContent(cleanSourceText(finding.source_text), lightTheme)}
              </div>
              {finding.source_url && (
                <div className="mt-2">
                  <ExternalLinkButton url={finding.source_url} label="View source document" />
                </div>
              )}
            </div>
          )}

          {/* EUR-Lex / PDF fallback when iframe URL unavailable */}
          {(sourceType === 'eurlex' || sourceType === 'pdf') && finding.source_text && (
            <div>
              <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Source text</div>
              <div className="p-2.5 bg-accent-tint/50 rounded-lg border border-accent-border text-xs leading-relaxed">
                {formatContent(cleanSourceText(finding.source_text), lightTheme)}
              </div>
              {eurLexUrl && (
                <div className="mt-2">
                  <ExternalLinkButton url={eurLexUrl} label="View full document on EUR-Lex" />
                </div>
              )}
            </div>
          )}

          {/* Uploaded document / workspace / unknown — generic source text.
              An upload-backed finding (category="upload", source_type="user_upload")
              carries a server-filled source_text (the cited span). Without this
              branch it would fall through to the empty state below even though
              the cited content is present. */}
          {(sourceType === 'unknown' || sourceType === 'user_upload' || sourceType === 'workspace') && finding.source_text && (
            <div>
              <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Source text</div>
              <div className="p-2.5 bg-surface rounded-lg border border-border-default text-xs">
                {formatContent(cleanSourceText(finding.source_text), lightTheme)}
              </div>
            </div>
          )}

          {/* No content available at all */}
          {!finding.source_text && !actionLink && (
            <div className="text-center py-8 text-sm text-fg-tertiary">
              No source content available for this finding.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConceptContent({ finding }: { finding: Finding }) {
  const definition = finding.source_snippet || finding.source_text || '';
  const sources = finding.definition_sources || [];
  const [activeSourceIdx, setActiveSourceIdx] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  // Reset loading state when switching source tabs
  useEffect(() => {
    setIframeLoading(true);
    setIframeError(false);
  }, [activeSourceIdx]);

  const activeSource = sources[activeSourceIdx];
  const viewerUrl = activeSource
    ? buildEurLexViewerUrl(activeSource.celex, activeSource.ref, findingAsOf(finding))
    : null;

  // Fallback: parse legacy source_reference for old data without definition_sources
  const legacySourceRefs = sources.length === 0
    ? (finding.source_reference || '').split(';').map(s => s.trim()).filter(Boolean)
    : [];

  return (
    <div className="h-full flex flex-col">
      {/* Definition text */}
      {definition && (
        <div className="flex-shrink-0 px-4 pt-3 pb-2">
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Definition</div>
          <div className="text-sm text-fg-secondary leading-relaxed bg-[color:var(--bark-purple-light)]/50 rounded-lg p-3 border border-purple-100">
            {cleanSourceText(definition)}
          </div>
        </div>
      )}

      {/* Source tabs + iframe (new structured data) */}
      {sources.length > 0 && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Source tabs */}
          <div className="flex-shrink-0 px-4 pt-1">
            <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Source</div>
            <div className="flex gap-1 flex-wrap">
              {sources.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveSourceIdx(i)}
                  className={`px-2.5 py-1 text-xs rounded-t border border-b-0 transition-colors ${
                    i === activeSourceIdx
                      ? 'bg-surface text-accent-hover border-border-default font-medium'
                      : 'bg-bg-warm text-fg-tertiary border-divider hover:bg-bg-warm'
                  }`}
                >
                  {src.ref} {src.short_name && `(${src.short_name})`}
                </button>
              ))}
            </div>
          </div>

          {/* EUR-Lex iframe */}
          {viewerUrl && (
            <div className="flex-1 relative mx-4 mb-3 border border-border-default rounded-b-lg overflow-hidden min-h-[200px]">
              {iframeLoading && !iframeError && (
                <SourceLoadingSkeleton sourceType="eurlex" />
              )}
              {iframeError && (
                <div className="absolute inset-0 flex items-center justify-center bg-bg-warm z-10">
                  <div className="text-center space-y-2">
                    <p className="text-xs text-fg-tertiary">Unable to load source</p>
                    <button
                      onClick={() => { setIframeLoading(true); setIframeError(false); }}
                      className="px-2 py-1 text-xs bg-accent text-white rounded hover:bg-accent-hover"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}
              <iframe
                key={`${activeSourceIdx}-${activeSource?.celex}`}
                src={viewerUrl}
                className="w-full h-full border-0"
                title={`Source: ${activeSource?.ref}`}
                sandbox={EURLEX_PREVIEW_SANDBOX}
                onLoad={() => { setIframeLoading(false); setIframeError(false); }}
                onError={() => { setIframeLoading(false); setIframeError(true); }}
              />
            </div>
          )}
        </div>
      )}

      {/* Fallback: legacy text-only sources (old findings without definition_sources) */}
      {sources.length === 0 && legacySourceRefs.length > 0 && (
        <div className="flex-shrink-0 px-4 pb-3">
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1.5">Defined in</div>
          <ul className="space-y-1">
            {legacySourceRefs.map((ref, i) => (
              <li key={i} className="text-xs text-fg-secondary flex items-start gap-1.5">
                <span className="text-node-definition mt-0.5">&#8226;</span>
                <span>{ref}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TAB: CONCEPT RELATED (for concept findings)
// =============================================================================

const RELATIONSHIP_LABELS: Record<string, string> = {
  is_a: 'Is a',
  consists_of: 'Consists of',
  applies_to: 'Applies to',
  subject_to: 'Subject to',
  related: 'Related to',
};

function ConceptRelatedTab({
  finding,
  allFindings,
  onFindingClick,
}: {
  finding: Finding;
  allFindings: Record<string, Finding>;
  onFindingClick: (findingId: string) => void;
}) {
  const relationships = finding.concept_relationships || {};
  const conceptName = finding.article_title || finding.source_document_name || '';

  // Find all findings that reference this concept
  const usageFindings = useMemo(() => {
    return Object.values(allFindings).filter(f =>
      f.finding_id !== finding.finding_id &&
      f.concepts?.includes(conceptName)
    );
  }, [allFindings, finding.finding_id, conceptName]);

  const hasRelationships = Object.keys(relationships).length > 0;
  const hasUsage = usageFindings.length > 0;

  if (!hasRelationships && !hasUsage) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-fg-tertiary">
        No relationships or usage found for this concept.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {/* Concept relationships */}
      {hasRelationships && (
        <div>
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Relationships</div>
          <div className="space-y-2">
            {Object.entries(relationships).map(([type, names]) => (
              <div key={type} className="flex items-start gap-2 text-xs">
                <span className="text-node-definition font-medium whitespace-nowrap min-w-[80px]">
                  {RELATIONSHIP_LABELS[type] || type}:
                </span>
                <div className="flex flex-wrap gap-1">
                  {names.map((name, i) => {
                    // Check if this related concept exists as a finding
                    const relFinding = Object.values(allFindings).find(f =>
                      f.source_type === 'concept' && (
                        f.article_title === name ||
                        f.source_document_name === name
                      )
                    );
                    return relFinding ? (
                      <button
                        key={i}
                        onClick={() => onFindingClick(relFinding.finding_id)}
                        className="inline-block px-2 py-0.5 rounded bg-[color:var(--bark-purple-light)] text-node-definition hover:bg-[color:var(--bark-purple-light)] transition-colors"
                      >
                        {name}
                      </button>
                    ) : (
                      <span
                        key={i}
                        className="inline-block px-2 py-0.5 rounded bg-bg-warm text-fg-secondary"
                      >
                        {name}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Where this concept is used */}
      {hasUsage && (
        <div>
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-2">
            Used in {formatCount(usageFindings.length, { one: 'finding', other: 'findings' })}
          </div>
          <ul className="space-y-1">
            {usageFindings.map(f => {
              const label = f.source_reference
                ? `${f.source_document_name || ''} ${f.source_reference}`.trim()
                : f.article_title || f.source_document_name || f.finding_id;
              return (
                <li key={f.finding_id} className="flex items-center gap-1.5 text-xs">
                  <Globe size={10} className="text-fg-tertiary flex-shrink-0" />
                  <button
                    onClick={() => onFindingClick(f.finding_id)}
                    className="text-accent hover:text-accent-hover hover:underline truncate text-left"
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TAB: LINKS
// =============================================================================

function LinksTab({
  finding,
  allFindings,
  workspaceId,
  compartmentId,
  onFindingClick,
}: {
  finding: Finding;
  allFindings: Record<string, Finding>;
  workspaceId?: string;
  compartmentId?: string;
  onFindingClick: (findingId: string) => void;
}) {
  const [inlinePreview, setInlinePreview] = useState<PreviewRef | null>(null);

  const outgoing = finding.cross_references?.filter(x => x.direction === 'outgoing') || [];
  const incoming = finding.cross_references?.filter(x => x.direction !== 'outgoing') || [];
  const allRefs = [...outgoing, ...incoming];
  const hasRefs = allRefs.length > 0;
  const hasQa = (finding.linked_qa?.length || 0) > 0;
  const hasConcepts = (finding.concepts?.length || 0) > 0;

  const findFindingForArticle = (article: string, celex: string): string | null => {
    if (!celex) return null;
    const cleaned = cleanArticleName(article);
    for (const f of Object.values(allFindings)) {
      if (f.source_document_celex !== celex) continue;
      if (f.source_reference === article || f.source_reference === cleaned) return f.finding_id;
      if (f.article_title === article || f.article_title === cleaned) return f.finding_id;
    }
    return null;
  };

  const findFindingForQa = (ref: string): string | null => {
    for (const f of Object.values(allFindings)) {
      if (f.source_reference === ref) return f.finding_id;
    }
    return null;
  };

  // Find the concept-type finding for a concept name
  const findConceptFinding = (conceptName: string): Finding | null => {
    for (const f of Object.values(allFindings)) {
      if (f.source_type === 'concept' && (
        f.article_title === conceptName ||
        f.source_document_name === conceptName ||
        f.source_reference === conceptName
      )) return f;
    }
    return null;
  };

  // Find all other findings that reference this concept
  const findFindingsWithConcept = (conceptName: string): Finding[] => {
    return Object.values(allFindings).filter(f =>
      f.finding_id !== finding.finding_id &&
      f.concepts?.includes(conceptName)
    );
  };

  // Concept popover state
  const [activeConceptPopover, setActiveConceptPopover] = useState<string | null>(null);
  const conceptPopoverRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!activeConceptPopover) return;
    const handleClick = (e: MouseEvent) => {
      if (conceptPopoverRef.current && !conceptPopoverRef.current.contains(e.target as Node)) {
        setActiveConceptPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeConceptPopover]);

  if (!hasRefs && !hasQa && !hasConcepts) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-fg-tertiary">
        No cross-references found for this finding.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* 3-column grid — scrollable, capped at 30% height */}
      <div className="grid grid-cols-3 gap-2 px-3 py-2 flex-shrink-0 border-b border-divider max-h-[30%] overflow-y-auto">
        {/* Column 1: References */}
        <div className="min-w-0">
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-2">References</div>
          {hasRefs ? (
            <ul className="space-y-1">
              {allRefs.map((xref, i) => {
                const findingId = findFindingForArticle(xref.article, xref.celex);
                const isOutgoing = xref.direction === 'outgoing';
                const label = cleanArticleName(xref.article);
                return (
                  <li key={i} className="flex items-center gap-1 text-xs">
                    {isOutgoing ? (
                      <ArrowUpRight size={10} className="text-link flex-shrink-0" />
                    ) : (
                      <ArrowDownLeft size={10} className="text-success flex-shrink-0" />
                    )}
                    {findingId ? (
                      <button
                        onClick={() => onFindingClick(findingId)}
                        className="text-accent hover:text-accent-hover hover:underline truncate text-left"
                      >
                        {label}
                      </button>
                    ) : xref.celex ? (
                      <button
                        onClick={() => setInlinePreview({
                          celex: xref.celex,
                          articleRef: label,
                          documentName: xref.document_name,
                          type: xref.celex === 'EBA_Single_Rulebook_QA' ? 'qa' : 'eurlex',
                        })}
                        className="text-accent hover:text-accent-hover hover:underline truncate text-left"
                      >
                        {label}
                      </button>
                    ) : (
                      <span className="text-fg-secondary truncate">{label}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[11px] text-fg-tertiary">None</p>
          )}
        </div>

        {/* Column 2: Q&A */}
        <div className="min-w-0">
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Q&A</div>
          {hasQa ? (
            <ul className="space-y-1">
              {(finding.linked_qa || []).map((qa, i) => {
                const findingId = findFindingForQa(qa.ref);
                const displayTitle = qa.title
                  ? (qa.title.length > 60 ? qa.title.substring(0, 60) + '...' : qa.title)
                  : qa.ref;
                return (
                  <li key={i} className="flex items-center gap-1 text-xs">
                    <MessageSquare size={10} className="text-warning flex-shrink-0" />
                    {findingId ? (
                      <button
                        onClick={() => onFindingClick(findingId)}
                        className="text-accent hover:text-accent-hover hover:underline truncate text-left"
                      >
                        {displayTitle}
                      </button>
                    ) : (
                      <button
                        onClick={() => setInlinePreview({
                          celex: 'EBA_Single_Rulebook_QA',
                          articleRef: qa.ref,
                          documentName: 'EBA Q&A',
                          type: 'qa',
                        })}
                        className="text-accent hover:text-accent-hover hover:underline truncate text-left"
                      >
                        {displayTitle}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[11px] text-fg-tertiary">None</p>
          )}
        </div>

        {/* Column 3: Concepts */}
        <div className="min-w-0 relative">
          <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-2">Concepts</div>
          {hasConcepts ? (
            <div className="flex flex-wrap gap-1">
              {(finding.concepts || []).map((concept, i) => {
                const conceptFinding = findConceptFinding(concept);
                const isActive = activeConceptPopover === concept;
                return (
                  <div key={i} className="relative">
                    <button
                      onClick={() => setActiveConceptPopover(isActive ? null : concept)}
                      className={`inline-block px-2 py-0.5 rounded text-[11px] transition-colors cursor-pointer ${
                        isActive
                          ? 'bg-[color:var(--bark-purple-light)] text-node-definition ring-1 ring-node-definition'
                          : 'bg-[color:var(--bark-purple-light)] text-node-definition hover:bg-[color:var(--bark-purple-light)]'
                      }`}
                    >
                      {concept}
                    </button>
                    {/* Concept popover */}
                    {isActive && (
                      <div
                        ref={conceptPopoverRef}
                        className="absolute z-30 top-full left-0 mt-1 w-64 bg-surface rounded-lg shadow-lg border border-border-default p-3 text-xs"
                      >
                        {/* Definition */}
                        {conceptFinding ? (
                          <div className="mb-2">
                            <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1">Definition</div>
                            <p className="text-fg-secondary leading-relaxed line-clamp-3">
                              {conceptFinding.source_snippet || conceptFinding.source_text?.substring(0, 200) || 'No definition available'}
                            </p>
                          </div>
                        ) : (
                          <p className="text-fg-tertiary mb-2 italic">No definition in workspace</p>
                        )}

                        {/* Findings referencing this concept */}
                        {(() => {
                          const refs = findFindingsWithConcept(concept);
                          return refs.length > 0 ? (
                            <div className="mb-2">
                              <div className="text-[10px] font-semibold text-fg-tertiary uppercase tracking-wide mb-1">
                                Referenced by {formatCount(refs.length, { one: 'finding', other: 'findings' })}
                              </div>
                              <ul className="space-y-0.5">
                                {refs.slice(0, 5).map(f => (
                                  <li key={f.finding_id}>
                                    <button
                                      onClick={() => { setActiveConceptPopover(null); onFindingClick(f.finding_id); }}
                                      className="text-accent hover:text-accent-hover hover:underline truncate block max-w-full text-left"
                                    >
                                      {f.source_reference || f.article_title || f.source_document_name || f.finding_id}
                                    </button>
                                  </li>
                                ))}
                                {refs.length > 5 && (
                                  <li className="text-fg-tertiary">+{refs.length - 5} more</li>
                                )}
                              </ul>
                            </div>
                          ) : null;
                        })()}

                        {/* Navigate to concept finding */}
                        {conceptFinding && (
                          <button
                            onClick={() => { setActiveConceptPopover(null); onFindingClick(conceptFinding.finding_id); }}
                            className="flex items-center gap-1 text-node-definition hover:text-node-definition font-medium mt-1"
                          >
                            <BookOpen size={11} />
                            View full definition
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] text-fg-tertiary">None</p>
          )}
        </div>
      </div>

      {/* Inline preview area */}
      {inlinePreview ? (
        <div className="flex-1 min-h-0">
          <ReferencePreview
            previewRef={inlinePreview}
            workspaceId={workspaceId || ''}
            compartmentId={compartmentId || 'default'}
            onClose={() => setInlinePreview(null)}
            onFindingCreated={(findingId) => onFindingClick(findingId)}
          />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-xs text-fg-tertiary">
          Click a reference above to preview it here
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SOURCE LOADING SKELETON — shown while the iframe fetches
// =============================================================================

/**
 * Skeleton-style loading state for the source iframe.
 * Replaces the previous "gray box + tiny spinner" with a proper content
 * silhouette (article-shaped placeholder bars) plus a staged status
 * indicator so the user sees something is happening and how long it
 * typically takes.
 */
function SourceLoadingSkeleton({ sourceType }: { sourceType: SourceType }) {
  const [stageIdx, setStageIdx] = useState(0);

  const stages = useMemo(() => {
    if (sourceType === 'pdf') {
      return [
        'Fetching PDF…',
        'Rendering pages…',
        'Almost there…',
      ];
    }
    // eurlex and everything else
    return [
      'Checking cache…',
      'Fetching article from EUR-Lex…',
      'Extracting content…',
      'Almost there…',
    ];
  }, [sourceType]);

  useEffect(() => {
    // Advance through stages on approximate real-world timings so the
    // message tracks what the backend is actually doing most of the time.
    const delays = sourceType === 'pdf'
      ? [700, 2000]
      : [600, 1500, 3000];
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    delays.forEach((d, i) => {
      timeouts.push(setTimeout(() => setStageIdx(i + 1), d));
    });
    return () => timeouts.forEach(clearTimeout);
  }, [sourceType]);

  // Cap at last stage
  const currentStage = stages[Math.min(stageIdx, stages.length - 1)];
  const isEurlex = sourceType === 'eurlex';
  const label = sourceType === 'pdf' ? 'Loading PDF' : 'Loading article';

  return (
    <div className="absolute inset-0 bg-bg z-10">
      {/* Dim skeleton silhouette behind the card — gives shape context */}
      <div className="absolute inset-0 overflow-hidden opacity-40">
        <div className="max-w-[760px] mx-auto p-6 pt-8">
          {isEurlex && (
            <>
              <div className="h-2.5 w-28 bg-bg-warm rounded animate-pulse mb-3" />
              <div className="h-5 w-3/5 bg-bg-warm rounded animate-pulse mb-7" />
            </>
          )}
          <div className="flex gap-3 items-start mb-5">
            <div className="h-2.5 w-3 bg-bg-warm rounded flex-shrink-0 animate-pulse mt-1.5" />
            <div className="flex-1 space-y-2">
              <div className="h-2.5 w-full bg-bg-warm rounded animate-pulse" />
              <div className="h-2.5 w-[96%] bg-bg-warm rounded animate-pulse" />
              <div className="h-2.5 w-[91%] bg-bg-warm rounded animate-pulse" />
              <div className="h-2.5 w-[73%] bg-bg-warm rounded animate-pulse" />
            </div>
          </div>
          <div className="flex gap-3 items-start mb-5">
            <div className="h-2.5 w-3 bg-bg-warm rounded flex-shrink-0 animate-pulse mt-1.5" />
            <div className="flex-1 space-y-2">
              <div className="h-2.5 w-full bg-bg-warm rounded animate-pulse" />
              <div className="h-2.5 w-[88%] bg-bg-warm rounded animate-pulse" />
              <div className="h-2.5 w-[62%] bg-bg-warm rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>

      {/* Centered loading card — prominent, unmissable */}
      <div className="absolute inset-0 flex items-center justify-center px-4 z-10">
        <div
          className="bg-bg border border-border-default rounded-lg shadow-bark-md px-5 py-4 w-full max-w-[320px]"
          role="status"
          aria-live="polite"
        >
          {/* Header row: spinning icon + bold label */}
          <div className="flex items-center gap-2.5 mb-3">
            <Loader2
              size={18}
              className="animate-spin flex-shrink-0"
              style={{ color: 'var(--accent)' }}
            />
            <span className="text-sm font-semibold text-fg">{label}</span>
          </div>

          {/* Indeterminate progress bar — sliding teal shard across a track */}
          <div className="relative h-1.5 bg-bg-warm rounded overflow-hidden mb-2.5">
            <div
              className="absolute inset-y-0 w-1/3 rounded bark-bar-slide"
              style={{
                background:
                  'linear-gradient(90deg, transparent 0%, var(--accent) 40%, var(--accent) 60%, transparent 100%)',
              }}
            />
          </div>

          {/* Current stage text */}
          <div className="flex items-center gap-2 text-[11px] text-fg-secondary">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: 'var(--accent)' }}
            />
            <span className="truncate">{currentStage}</span>
          </div>

          {/* Timing hint */}
          <div className="mt-2 pt-2 border-t border-divider text-[10px] text-fg-tertiary flex items-center justify-between">
            <span>Fetching from authoritative source</span>
            <span>~3–5s</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// HEADER HELPERS — for the redesigned finding header
// =============================================================================

/** Source-type accent color (CSS var for easy restyling) */
function sourceTypeAccent(type: SourceType): string {
  switch (type) {
    case 'eurlex': return 'var(--bark-teal)';
    case 'pdf': return 'var(--bark-cyan)';
    case 'qa': return 'var(--bark-orange)';
    case 'concept': return 'var(--bark-purple)';
    default: return 'var(--fg-tertiary)';
  }
}

/** Derive the kicker label from source type + legal_status */
function deriveKicker(finding: Finding, sourceType: SourceType): string {
  if (sourceType === 'eurlex') {
    if (finding.legal_status === 'interpretive') return 'EU Regulation · Interpretive';
    if (finding.legal_status === 'comply_or_explain') return 'EU Regulation · Comply or explain';
    return 'EU Regulation · Binding';
  }
  if (sourceType === 'pdf') {
    const auth = finding.authority_abbrev || finding.authority_name_en || 'Guidance';
    const bindness = finding.legal_status === 'binding' ? 'Binding' : 'Non-binding';
    return `${auth} · ${bindness}`;
  }
  if (sourceType === 'qa') return 'Q&A · Provider-issued answer';
  if (sourceType === 'concept') return 'Concept · Regulatory definition';
  if (sourceType === 'user_upload') return 'Uploaded document';
  return 'Source';
}

// =============================================================================
// MAIN DETAIL VIEW
// =============================================================================

export default function FindingDetailView({
  finding, citationContext, allFindings, allMarkers, workspaceId, compartmentId, onNavigateToNode, onFindingClick,
}: FindingDetailViewProps) {
  const [activeTab, setActiveTab] = useState<'finding' | 'links'>('finding');
  const [isFocusOpen, setIsFocusOpen] = useState(false);
  const sourceType = detectFindingSourceType(finding);
  const eurLexUrl = buildEurLexUrl(finding.source_document_celex, {
    articleRef: finding.source_reference,
    asOf: findingAsOf(finding),
  });
  const peerDocumentUrl = useMemo(() => {
    if (sourceType !== 'pdf') return null;
    return findPeerDocumentUrl(
      finding.finding_id,
      finding.source_document_celex,
      finding.source_document_name,
      allFindings,
    );
  }, [
    sourceType,
    finding.finding_id,
    finding.source_document_celex,
    finding.source_document_name,
    allFindings,
  ]);

  // Build action link
  const actionLink = useMemo(() => {
    const safeSourceUrl = getSafeSourceUrl(finding.source_url);
    const safePdfUrl = getSafeSourceUrl(finding.pdf_url);
    const safePageUrl = getSafeSourceUrl(finding.page_url);

    if (sourceType === 'eurlex' && eurLexUrl) return { url: eurLexUrl, label: 'View source document' };
    if (sourceType === 'qa' && safeSourceUrl) return { url: safeSourceUrl, label: 'View source document' };
    if (sourceType === 'pdf') {
      const url = safeSourceUrl || safePdfUrl || safePageUrl || peerDocumentUrl;
      if (url) return { url, label: 'View source document' };
      // Fallback: open PDF in new tab via proxy
      const docId = extractPdfDocIdFromFields(finding.chunk_id, finding.source_document_celex);
      if (docId) {
        const proxyUrl = buildPdfViewerUrl(docId, null);
        if (proxyUrl) return { url: proxyUrl, label: 'Open PDF' };
      }
    }
    if (safeSourceUrl) return { url: safeSourceUrl, label: 'View source document' };
    return null;
  }, [
    sourceType,
    eurLexUrl,
    finding.source_url,
    finding.pdf_url,
    finding.page_url,
    finding.chunk_id,
    finding.source_document_celex,
    peerDocumentUrl,
  ]);

  // All markers referencing this finding
  const findingMarkers = useMemo(() => {
    if (!allMarkers) return [];
    return Object.values(allMarkers).filter(m => m.finding_id === finding.finding_id);
  }, [allMarkers, finding.finding_id]);


  // Fallback single reference — the agent's quote in full, never
  // character-truncated (the containing section scrolls).
  const fallbackReference = useMemo(() => {
    if (findingMarkers.length > 0) return null;
    if (citationContext?.citation_text) return citationContext.citation_text;
    if (sourceType === 'concept') return null;
    const raw = resolveAgentReferenceText(finding.source_text, finding.source_snippet);
    return raw ? cleanSourceText(raw) || null : null;
  }, [findingMarkers.length, citationContext, finding.source_text, finding.source_snippet, sourceType]);

  // Count links for tab badge
  const isConcept = sourceType === 'concept';
  const isPdf = sourceType === 'pdf';
  const conceptRelCount = isConcept
    ? Object.values(finding.concept_relationships || {}).reduce((sum, arr) => sum + arr.length, 0)
    : 0;
  const conceptUsageCount = isConcept
    ? Object.values(allFindings).filter(f => f.finding_id !== finding.finding_id && f.concepts?.includes(finding.article_title || finding.source_document_name || '')).length
    : 0;
  // PDF context count: count non-empty metadata sections
  const pdfContextCount = isPdf ? (
    (finding.regulatory_framework?.length ? 1 : 0) +
    (finding.legal_basis?.length ? 1 : 0) +
    (finding.applies_to?.length ? 1 : 0) +
    (finding.legal_status ? 1 : 0) +
    (finding.parent_reference || finding.replaces || finding.amends?.length ? 1 : 0) +
    (finding.publication_date || finding.effective_date || finding.compliance_deadline ? 1 : 0)
  ) : 0;
  const linksCount = isConcept
    ? conceptRelCount + conceptUsageCount
    : isPdf
      ? pdfContextCount
      : (finding.cross_references?.length || 0) + (finding.linked_qa?.length || 0) + (finding.concepts?.length || 0);

  // Redesigned header bits
  const kicker = deriveKicker(finding, sourceType);
  const accent = sourceTypeAccent(sourceType);
  const displayTitle = useMemo(() => {
    const rawTitle =
      finding.article_title ||
      (sourceType === 'concept' ? finding.source_document_name : '') ||
      finding.source_document_name ||
      finding.finding_id;
    return stripLeadingReference(rawTitle, finding.source_reference);
  }, [
    finding.article_title,
    finding.finding_id,
    finding.source_document_name,
    finding.source_reference,
    sourceType,
  ]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Sticky header — compact, narrow-friendly */}
      <div className="flex-shrink-0 relative border-b border-border-default">
        {/* 3px source-type accent bar, full-height on the left */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ background: accent }}
          aria-hidden
        />
        <div className="pl-3 pr-3 pt-2.5 pb-2.5">
          {/* Top row: kicker (left) + primary action (right). Kicker shrinks,
              action stays tight. Keeps header from ballooning at narrow width. */}
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: accent }}
                aria-hidden
              />
              <span
                className="text-[10px] font-semibold uppercase tracking-wide truncate"
                style={{ color: accent }}
                title={kicker}
              >
                {kicker}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Explore deeper — opens a full-screen focus view with the
                  embedded article plus a rail of cross-refs / Q&A /
                  concepts / lineage. Inline panel is unchanged. */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setIsFocusOpen(true); }}
                className="inline-flex items-center gap-1 px-2 py-1 border border-accent/60 text-accent-hover hover:bg-accent-tint rounded text-[11px] font-semibold transition-colors"
                title="Explore finding deeper — open full-screen focus view"
                aria-label="Explore finding deeper — open full-screen focus view"
              >
                <Maximize2 size={11} />
                <span className="hidden sm:inline">Explore deeper</span>
              </button>
              {actionLink && (
                <a
                  href={actionLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 bg-accent text-white hover:bg-accent-hover rounded text-[11px] font-semibold transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  title={actionLink.label}
                >
                  <Globe size={11} />
                  <span className="hidden sm:inline">Open source</span>
                </a>
              )}
            </div>
          </div>
          {displayTitle && (
            <h2 className="font-body text-[15px] sm:text-[17px] font-semibold leading-[1.25] tracking-normal text-fg break-words">
              {displayTitle}
            </h2>
          )}
        </div>
      </div>

      {/* Tab bar: Finding + Links */}
      <div className="flex border-b border-border-default px-4 mt-2 flex-shrink-0">
        <button
          onClick={() => setActiveTab('finding')}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
            activeTab === 'finding'
              ? 'border-accent text-accent-hover'
              : 'border-transparent text-fg-tertiary hover:text-fg-secondary'
          }`}
        >
          Finding
        </button>
        {linksCount > 0 && (
          <button
            onClick={() => setActiveTab('links')}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === 'links'
                ? 'border-accent text-accent-hover'
                : 'border-transparent text-fg-tertiary hover:text-fg-secondary'
            }`}
          >
            {isConcept ? 'Related' : isPdf ? 'Context' : 'Related articles, Q&A, etc.'}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-bg-warm text-fg-tertiary">
              {linksCount}
            </span>
          </button>
        )}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === 'finding' && (
          <FindingTab
            finding={finding}
            citationContext={citationContext}
            findingMarkers={findingMarkers}
            fallbackReference={fallbackReference}
            sourceType={sourceType}
            actionLink={actionLink}
            eurLexUrl={eurLexUrl}
          />
        )}
        {activeTab === 'links' && (
          isConcept ? (
            <ConceptRelatedTab
              finding={finding}
              allFindings={allFindings}
              onFindingClick={onFindingClick}
            />
          ) : isPdf ? (
            <PdfContextTab finding={finding} />
          ) : (
            <LinksTab
              finding={finding}
              allFindings={allFindings}
              workspaceId={workspaceId}
              compartmentId={compartmentId}
              onFindingClick={onFindingClick}
            />
          )
        )}
      </div>

      {/* Full-screen focus modal — opened from "Explore deeper" header action.
          Dismissing it drops back into the inline detail view unchanged. */}
      <FindingFocusModal
        isOpen={isFocusOpen}
        finding={finding}
        citationContext={citationContext}
        allFindings={allFindings}
        allMarkers={allMarkers}
        workspaceId={workspaceId}
        compartmentId={compartmentId}
        onClose={() => setIsFocusOpen(false)}
        onFindingClick={onFindingClick}
      />
    </div>
  );
}
